// Footer.tsx (replace the outer Box styles + spacer logic)
import React, {
  Children, ReactElement, cloneElement, isValidElement, useLayoutEffect, useRef, useState
} from 'react';
import {
  Box, Button, CircularProgress, useTheme, useMediaQuery, type ButtonProps,
  Container,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

interface AppFooterProps {
  onSubmit?: () => void;
  disabled?: boolean;
  loading?: boolean;
  label?: string;
  children?: React.ReactNode;
  /** Rendered above the button row, inside the footer container. */
  topContent?: React.ReactNode;
  position?: 'fixed' | 'sticky' | 'static';
  elevated?: boolean;
  reserveSpace?: boolean;
}

const Footer: React.FC<AppFooterProps> = ({
  onSubmit,
  disabled = false,
  loading = false,
  label = 'Submit',
  children,
  topContent,
  position = 'fixed',
  elevated = false,
  reserveSpace = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const glassBg = alpha(theme.palette.primary.light, 0.05);

  const ref = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState<number>(112); // a bit larger default

  useLayoutEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const update = () => {
      const h = el.getBoundingClientRect().height;
      if (h && h !== height) setHeight(h);
    };
    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [height]);

  const baseButtonSx = {
    py: { xs: 1.5, sm: 1.5, md: 1.75, lg: 2, xl: 2.5 },
    fontSize: { xs: 14, sm: 16, md: 17, lg: 18, xl: 20 },
    borderRadius: 2,
    flex: isMobile ? 0 : 1,
    textTransform: 'none',
    fontWeight: 700,
    '&.Mui-disabled': {
      opacity: 0.6,
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.action.disabledBackground,
    },
  };


  const styledChildren = children
    ? Children.map(children, (child) => {
      if (!isValidElement<ButtonProps>(child)) return child;
      const isMuiButton = child.type === Button;

      if (!isMuiButton) return child;

      const btn = child as ReactElement<ButtonProps>;
      const childDisabled = disabled || loading || btn.props.disabled;
      return cloneElement(
        btn,
        {
          fullWidth: isMobile,
          disabled: childDisabled,
          sx: { ...baseButtonSx, ...(btn.props.sx || {}) },
        } as Partial<ButtonProps>
      );
    })
    : null;

  return (
    <>
      {/* Spacer to keep content above fixed footer */}
      {reserveSpace && position === 'fixed' && (
        <Box sx={{ height }} />
      )}

      <Box
        ref={ref}
        component="footer"
        sx={{
          position,
          bottom: 0,
          left: 0,
          width: '100%',                              // <-- important: no 100vw
          bgcolor: 'background.paper',
          backgroundImage: `linear-gradient(${glassBg}, ${glassBg})`,
          backdropFilter: 'saturate(120%) blur(6px)',
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
          zIndex: theme.zIndex.appBar,
          boxShadow: elevated ? '0 -8px 24px rgba(0,0,0,0.08)' : 'none',
        }}
      >
        <Container
          disableGutters
          sx={{
            width: '100%',
            maxWidth: { xs: '100%', sm: 720, md: 880 },
            mx: 'auto',
          }}
        >
          {topContent && <Box sx={{ mx: 1, px: 2, pt: 2 }}>{topContent}</Box>}
          <Box
            sx={{
              mx: 1,
              my: 1,
              p: 2,
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
            }}
          >
            {styledChildren ?? (
              <Button
                fullWidth
                variant="contained"
                onClick={onSubmit}
                disabled={disabled || loading}
                sx={baseButtonSx}
                startIcon={
                  loading ? <CircularProgress size={20} thickness={5} sx={{ mr: -0.5 }} /> : undefined
                }
              >
                {loading ? 'Please wait…' : label}
              </Button>
            )}
          </Box>
        </Container>

      </Box>
    </>
  );
};

export default Footer;
